import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {useMap} from 'react-map-gl';
import { useTranslation } from 'react-i18next';

import { callGetJson } from '../api/jsonConfApi';
import { config } from '../config.js';
import { initLayersConf } from '../store/actions/layerActions';
import { unselectFeature } from '../store/actions/selectedFeatureActions';
import { setContentType, setOpenSideBar } from '../store/actions/sideBarActions';

const profilLayersMapping = {
  'Marche' : {
    'fr': 'fr/custom_layers_walking.json',
    'en': 'en/custom_layers_walking.json',
    'es': 'es/custom_layers_walking.json'
  },
  'Déficience motrice': {
    'fr': 'fr/custom_layers_wheelchair.json',
    'en': 'en/custom_layers_wheelchair.json',
    'es': 'es/custom_layers_wheelchair.json'
  },
  'Déficience visuelle': {
    'fr': 'fr/custom_layers_visually_impaired.json',
    'en': 'en/custom_layers_visually_impaired.json',
    'es': 'es/custom_layers_visually_impaired.json'
  },
  'Déficience auditive': {
    'fr': 'fr/custom_layers_hearing_impaired.json',
    'en': 'en/custom_layers_hearing_impaired.json',
    'es': 'es/custom_layers_hearing_impaired.json'
  },
  'Déficience mentale': {
    'fr': 'fr/custom_layers_mentally_impaired.json',
    'en': 'en/custom_layers_mentally_impaired.json',
    'es': 'es/custom_layers_mentally_impaired.json',
  }
};

const generateButtonsLayersState = (layerInfos) => {
  return {
    layersName: layerInfos.layersName,
    label: layerInfos.layerLabel,
    active: layerInfos.visible,
    legendIcons: layerInfos.legendIcons.map((icon) => {
      return config.url.LAYER_ICONS_CONF_URL + '/layerIcons/' + icon;
    }),
    pngIcons: layerInfos.pngIcons.map((icon) => {
      return config.url.LAYER_ICONS_CONF_URL + '/layerIcons/' + icon;
    }),
    legendLabels: layerInfos.legendLabels,
    blackPngIcon: layerInfos.blackPngIcon !== '' ? config.url.LAYER_ICONS_CONF_URL + '/layerIcons/' + layerInfos.blackPngIcon: '',
    whitePngIcon: layerInfos.whitePngIcon !== '' ? config.url.LAYER_ICONS_CONF_URL + '/layerIcons/' + layerInfos.whitePngIcon: '',
    sdf: layerInfos.sdf
  };
};

export const LayersEngine = ({ isMobile }) => {
  const dispatch = useDispatch();
  const {mymap} = useMap();
  const { t, i18n } = useTranslation();
  const getLanguage = () => i18n.language || window.localStorage.i18nextLng;

  const profil = useSelector(state => state.profils.profil);
  const layerConf = useSelector(state => state.layerConf);
  const [profilLayers, setProfilLayers] = useState({});
  const [buttonsLayersState, setButtonsLayersState] = useState({});
  const [getFeatureInfoAttrs, setGetFeatureInfoAttrs] = useState({});
  const [layerList, setLayerList] = useState([]);
  const [sourceList, setSourceList] = useState({});
  const [update, setUpdate] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(unselectFeature());
      dispatch(setOpenSideBar(false));
      if (isMobile) {
        await new Promise(res => setTimeout(res, 50));
      }
      dispatch(setContentType('direction'));

      let result = await callGetJson(`${getLanguage()}/custom_layers.json`);
      let buttonsLayersState = {};

      result['iconsAndButtonsLayers'].forEach(layerInfos => {
        buttonsLayersState[layerInfos.layersName[0]] = generateButtonsLayersState(layerInfos);
      });
      setButtonsLayersState(buttonsLayersState);

      setSourceList(result['sources']);
      setLayerList(result['layers']);
      setGetFeatureInfoAttrs(result['getFeatureInfoAttrs']);
      let newProfilLayers = {...profilLayers};
      for (const profil in profilLayersMapping) {
        let result = await callGetJson(profilLayersMapping[profil][getLanguage()]);
        let buttonsProfilLayersState = {};
        result['iconsAndButtonsLayers'].forEach(layerInfos => {
          buttonsProfilLayersState[layerInfos.layersName[0]] = generateButtonsLayersState(layerInfos);
        });
        newProfilLayers[profil] = {
          'iconsAndButtonsLayers': buttonsProfilLayersState,
          'layers': result['layers'],
          'sources': result['sources'],
          'getFeatureInfoAttrs': result['getFeatureInfoAttrs'],
          'base_layer': result['base_id']
        };
      }
      setProfilLayers(newProfilLayers);
      return 'Layers config fetched !';
    };

    fetchData().then(() => {
      setUpdate(update + 1);
    }).catch(error => console.log('error fetch data', error));

  }, [t]);

  useEffect(() => {
    if (update > 0) {
      setUpdate(update + 1);
    }
  }, [profil]);

  useEffect(() => {
    
    if (update > 0) {

      let newInit = {
        'buttonsLayersState': {...profilLayers[profil].iconsAndButtonsLayers, ...buttonsLayersState},
        'getFeatureInfoAttrs': {...profilLayers[profil].getFeatureInfoAttrs, ...getFeatureInfoAttrs},
        'sources': {...profilLayers[profil].sources, ...sourceList},
        'layers': [...profilLayers[profil].layers, ...layerList],
        'base_layer': profilLayers[profil].base_layer
      };

      for (const key in layerConf.buttonsLayersState) {
        if (Object.keys(newInit.buttonsLayersState).includes(key)) {
          newInit.buttonsLayersState[key].active = layerConf.buttonsLayersState[key].active;
        }
      }
      dispatch(initLayersConf(newInit));
    }
  }, [update]);

  useEffect(() => {
    let iconsToLoad = {};
    if (mymap !== undefined) {
      for (const key in buttonsLayersState) {
        buttonsLayersState[key].pngIcons.forEach((iconPath) => {
          const iconName = iconPath.split('/').pop().split('.')[0];
          if (!Object.keys(iconsToLoad).includes(iconName)) {
            iconsToLoad[iconName]={
              'iconPath': iconPath,
              'sdf': buttonsLayersState[key].sdf,
            };
          }
        });
      }
      for (const profil in profilLayers) {
        for (const key in profilLayers[profil]['iconsAndButtonsLayers']) {
          profilLayers[profil]['iconsAndButtonsLayers'][key].pngIcons.forEach((iconPath) => {
            const iconName = iconPath.split('/').pop().split('.')[0];
            if (!Object.keys(iconsToLoad).includes(iconName)) {
              iconsToLoad[iconName]={
                'iconPath': iconPath,
                'sdf': profilLayers[profil]['iconsAndButtonsLayers'][key].sdf,
              };
            }
          });
        }
      }
      for (const iconName in iconsToLoad) {
        if (!mymap.hasImage(iconName)) {
          mymap.loadImage(iconsToLoad[iconName].iconPath, (error, image) => {
            mymap.addImage(iconName, image, { sdf: iconsToLoad[iconName].sdf });
          });
        }
      }
    }
  }, [mymap, update]);

  return (null);
};
